import { useCallback, useRef, useState } from "react";

import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../../../../service/query";
import { ServiceError, Status } from "../../../../../service/Shared";
import { getErrorMessageFromCode } from "../../../../../service/ValidationErrorFormatter";
import { updateWorkflowTask } from "../../../../../service/workflow";
import { CompleteTaskRepeatSource, getTaskVisibilityFromAcl } from "../../../../../templates/WorkflowTaskPanel";
import { KanbanColumnId, KanbanTask, KanbanTaskDrop, Toast } from "../../../../../widget";
import { COMPLETED_COLUMN_ID, UNASSIGNED_COLUMN_ID } from "../../../../../widget/general/TasksDashboard/constants";
import { buildTaskByUuidRequest, mapColumnIdToStatus } from "../utils";

type WorkflowTaskNode = SearchWorkflowTasksResponse["results"][number];

export interface UseTasksDashboardTaskMoveArgs {
  /** The loaded cards — the dragged tasks' names, and the lane each is currently in. */
  tasks: KanbanTask[];
  /** Reloads the dashboard's queries. The channel a failed card is reverted through. */
  refresh: () => void;
}

export interface UseTasksDashboardTaskMoveReturnData {
  /** The board's drop, as one action: the whole dragged block with its target lane. */
  onTaskDrop: (drop: KanbanTaskDrop) => void;
  /** True while the "Complete task" modal is collecting the Resolution for a drop into Complete. */
  showCompleteTaskModal: boolean;
  /** What the modal names as the task being completed — the card's title, or "N tasks". */
  completeTaskLabel: string;
  /**
   * Applies the collected Resolution to every card in the pending drop. Answers with one repeat source
   * per repeatable general task it completed, in drop order, which keeps the modal open on its repeat
   * step (REQ-008); with none, the modal is closed here.
   */
  handleSubmitResolution: (resolution: string) => Promise<CompleteTaskRepeatSource[]>;
  /** Cancels the drop — the cards return to the lanes they were dragged from. */
  handleCancelComplete: () => void;
}

/** The reason a single update was rejected, for the message naming the task it belongs to. */
const getFailureReason = (errors: ServiceError[] | undefined): string => {
  const error = errors?.[0];
  if (!error) return "please try again later.";

  return getErrorMessageFromCode(error.code, error.parameters);
};

/**
 * The task's current record, or `undefined` if it couldn't be read. Both failure modes are folded
 * together — an error status and a rejected request — because the caller's answer to them is the same:
 * don't update a task whose record you don't have.
 */
const fetchTaskNode = async (taskId: string): Promise<WorkflowTaskNode | undefined> => {
  try {
    const response = await searchWorkflowTasks(buildTaskByUuidRequest(taskId));

    return response.status === Status.Success ? response.data?.results[0] : undefined;
  } catch {
    return undefined;
  }
};

/** As above, for the ACL read: a rejection is the same "couldn't read it" as an error status. */
const readVisibility = async (node: WorkflowTaskNode): Promise<string[] | undefined> => {
  try {
    return await getTaskVisibilityFromAcl(node.uuid, node.ownerOrganisation.uuid);
  } catch {
    return undefined;
  }
};

/** A task the drop successfully updated, as the record and audience it was updated from. */
interface UpdatedTask {
  node: WorkflowTaskNode;
  visibility: string[];
}

/*
  REQ-008 — the next instance of a completed general task is cloned from the record the completion was
  sent from, so the drop's own read is reused rather than the task being fetched again. Only a general
  task (no workflow step) is repeatable; the clone hangs off the same parent object and has to be
  readable by the same organisations, so a task missing either isn't offered — the same conditions the
  task panel's complete path applies.
*/
const buildRepeatSource = ({ node, visibility }: UpdatedTask): CompleteTaskRepeatSource | undefined => {
  const isGeneralTask = node.step?.uuid == null;

  if (!isGeneralTask || !node.objectType || !node.objectUuid || visibility.length === 0) return undefined;

  return {
    task: {
      name: node.name,
      description: node.description,
      isMandatory: node.isMandatory,
      isMilestone: node.isMilestone,
      keyDate: node.keyDate,
      objectType: node.objectType,
      objectUuid: node.objectUuid,
      ownerOrganisationUuid: node.ownerOrganisation.uuid,
      repeat: node.repeat,
    },
    visibility,
  };
};

/**
 * Drag-to-restatus for the Kanban board (KAN-9562).
 *
 * Every drop is `PATCH /workflow/task` with the task's full record and the lane's status — the same
 * update the task panel makes, so the backend applies the same rules to a drag as to an edit. Two
 * things the board can't supply are read per task first:
 *
 *  - the **full record**. A card carries only what it renders, and the update rewrites every field —
 *    so the task is refetched and the request rebuilt from it, rather than from the card. The refetch
 *    also carries the current `rowVersion`, which is what the update is concurrency-checked on
 *    (BR-02800): a version from whenever the lane last loaded would fail against anyone else's change.
 *  - the **visibility**, which the search results don't return at all. See `getTaskVisibilityFromAcl`.
 *
 * A multi-select drag is N independent updates, never a batch: one failure reverts only its own card
 * and says why, and the rest of the selection stands (REQ-005).
 *
 * Once the updates have settled the whole dashboard is refreshed, rather than the two lanes the move
 * spanned. Reloading only those lanes needs the board's optimistic state mirrored here to know which
 * they are, and needs every partial reload ordered against the full loads, the other partial reloads
 * and the filter they were issued under — a set of races with no natural end. Re-reading the lanes is
 * four extra queries on a deliberate user action that already costs two round-trips per card, and it
 * leaves one source of truth: whatever the server last said.
 */
export const useTasksDashboardTaskMove = ({
  tasks,
  refresh,
}: UseTasksDashboardTaskMoveArgs): UseTasksDashboardTaskMoveReturnData => {
  const [pendingCompleteIds, setPendingCompleteIds] = useState<string[]>([]);
  const [showCompleteTaskModal, setShowCompleteTaskModal] = useState(false);

  // Read inside the handlers so they stay stable while the board re-renders through a drag.
  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  /*
    The cards whose update hasn't come back yet. Until it does, `tasks` still shows them in the lane
    they left, so a second drop of the same card would be judged against a lane it isn't in any more —
    and would send a competing update against the row version the first one is replacing. They are
    simply not draggable again until they settle.
  */
  const inFlightRef = useRef(new Set<string>());

  const getTask = useCallback((taskId: string): KanbanTask | undefined => {
    return tasksRef.current.find((task) => task.id === taskId);
  }, []);

  /**
   * One task's update: refetch the record, rebuild the request from it, send it with the new status.
   * Answers with the record and audience it was sent from, which is what a repeat is cloned from.
   */
  const updateTaskStatus = useCallback(
    async (taskId: string, toStatus: KanbanColumnId, resolution: string | null): Promise<UpdatedTask> => {
      const name = getTask(taskId)?.title ?? "task";

      /*
        Both reads can reject outright — a network drop, or an Apollo error — as well as answering with
        an error status. A rejection here would be swallowed by the `allSettled` around the batch, so
        the card would snap back with nothing said; caught, it reads as the failure it is.
      */
      const node = await fetchTaskNode(taskId);
      if (!node) {
        Toast.error({ message: `Couldn't move "${name}" - please try again later.` });
        throw new Error(taskId);
      }

      /*
        The update rewrites the audience from what is sent, so a task whose ACL couldn't be read has to
        be left alone: the fallback list would be indistinguishable from a task shared with nobody, and
        sending it would revoke every other organisation's access while the move reported success.
      */
      const visibility = await readVisibility(node);
      if (!visibility) {
        Toast.error({ message: `Couldn't move "${name}" - couldn't read who the task is shared with.` });
        throw new Error(taskId);
      }

      /*
        An assignee and the Unassigned status are two sides of one fact (BR-02229/BR-02791), so a task
        landing in that lane is cleared rather than left assigned — the same pair the panel's own Clear
        action writes. The reminder goes with it: a reminder requires an assignee (BR-02794).
      */
      const isUnassigning = toStatus === UNASSIGNED_COLUMN_ID;

      /*
        A finished task has nothing left to be reminded about, so completing it drops any scheduled
        reminder — the same thing `useCompleteTaskModal` does, so that completing by drag and completing
        through the panel leave the task in the same state.
      */
      const clearsReminder = isUnassigning || toStatus === COMPLETED_COLUMN_ID;

      const response = await updateWorkflowTask({
        assignedToUserUuid: isUnassigning ? null : (node.assignee?.uuid ?? null),
        deadlineDate: node.deadlineDate,
        description: node.description,
        isMandatory: node.isMandatory,
        isMilestone: node.isMilestone,
        keyDate: node.keyDate,
        name: node.name,
        // The task's *own* object — a general task's parent, or a workflow task's linked activity.
        // Not `parentObject*`, which is the uniform parent the card links to and is not writable.
        objectType: node.objectType,
        objectUuid: node.objectUuid,
        ownerOrganisationUuid: node.ownerOrganisation.uuid,
        reminderDate: clearsReminder ? null : node.reminderDate,
        reminderType: clearsReminder ? null : node.reminderType,
        repeat: node.repeat,
        /*
          Only ever set when the task is being dropped into Complete. A task dragged *out* of Complete
          keeps null here and the backend clears the stored resolution itself, so the drag doesn't have
          to know which transitions drop it.
        */
        resolution,
        rowVersion: node.rowVersion,
        status: mapColumnIdToStatus(toStatus),
        visibility,
        // Null (not "") for a general task — the backend routes on WorkflowStepUuid == null.
        workflowStepUuid: node.step?.uuid ?? null,
        workflowTaskUuid: node.uuid,
      });

      if (response.status === Status.Error) {
        Toast.error({ message: `Couldn't move "${name}" - ${getFailureReason(response.errors)}` });
        throw new Error(taskId);
      }

      return { node, visibility };
    },
    [getTask]
  );

  /**
   * The whole dropped block, one update each. They are settled rather than raced so that a rejection
   * doesn't abandon the updates still in flight — a multi-select drop is N independent moves, and one
   * card failing must leave the others alone (REQ-005). Answers with the ones that were updated.
   */
  const moveTasks = useCallback(
    async (taskIds: string[], toStatus: KanbanColumnId, resolution: string | null): Promise<UpdatedTask[]> => {
      taskIds.forEach((taskId) => inFlightRef.current.add(taskId));

      let results: PromiseSettledResult<UpdatedTask>[] = [];

      try {
        /*
          Only failures are announced. A move that worked is already on screen — the card is in the lane
          the user dropped it into — so a toast per drag would narrate what they just did, and a
          multi-select drag would stack several of them.
        */
        results = await Promise.allSettled(taskIds.map((taskId) => updateTaskStatus(taskId, toStatus, resolution)));
      } finally {
        taskIds.forEach((taskId) => inFlightRef.current.delete(taskId));
      }

      /*
        Refresh whether or not anything succeeded. The board moved the cards optimistically the moment
        they were dropped and the reloaded queries are the only channel back: they re-derive the task
        list, which the board re-syncs from — so a card whose update failed snaps back to the lane it
        came from, and one that succeeded stays put with a fresh rowVersion.
      */
      refresh();

      return results.flatMap((result) => (result.status === "fulfilled" ? result.value : []));
    },
    [updateTaskStatus, refresh]
  );

  const onTaskDrop = useCallback(
    ({ taskIds, toStatus }: KanbanTaskDrop): void => {
      if (taskIds.length === 0) return;

      /*
        A card dropped into the lane it is already in has been reordered, not restatused — the board
        reports the drop either way, and position within a lane isn't persisted. Sending an update for
        it would be a write that changes nothing, and on the Complete lane it is worse than that: it
        would reopen the modal and overwrite a resolution the task already has. A card still waiting on
        an earlier drop is left alone for the same reason: its lane here is the one it has left.
      */
      const movingIds = taskIds.filter(
        (taskId) => !inFlightRef.current.has(taskId) && getTask(taskId)?.status !== toStatus
      );

      if (movingIds.length === 0) return;

      /*
        Complete needs a Resolution (BR-02793), so the drop is held until the modal has collected one
        rather than sent and rejected. Cancelling it is what returns the cards to their own lanes.
      */
      if (toStatus === COMPLETED_COLUMN_ID) {
        setPendingCompleteIds(movingIds);
        setShowCompleteTaskModal(true);
        return;
      }

      moveTasks(movingIds, toStatus, null).catch(() => {
        // Every failure has already been reported per task; this only stops the rejection escaping.
      });
    },
    [getTask, moveTasks]
  );

  const handleSubmitResolution = useCallback(
    async (resolution: string): Promise<CompleteTaskRepeatSource[]> => {
      /*
        The modal stays up until the updates have settled — its own submit button is what this promise
        holds in its loading state. Closing first would hand the board back while the completion was
        still running, and dragging the same card again from there would send a second update against
        the row version the first one is in the middle of replacing.
      */
      const updated = await moveTasks(pendingCompleteIds, COMPLETED_COLUMN_ID, resolution);

      setPendingCompleteIds([]);

      /*
        REQ-008 — a general task completed by drag is repeatable, the same as one completed through the
        task panel, so the modal is handed a source per general task in the drop and left open to work
        through them. A mixed drop offers only its general tasks; the workflow tasks in it are simply
        complete. Nothing is offered for a card whose own update failed — it is about to snap back to the
        lane it came from, so there is no completed task to take the next instance of.
      */
      const repeatSources = updated.flatMap((updatedTask) => buildRepeatSource(updatedTask) ?? []);

      if (repeatSources.length > 0) return repeatSources;

      setShowCompleteTaskModal(false);

      return [];
    },
    [pendingCompleteIds, moveTasks]
  );

  const handleCancelComplete = useCallback((): void => {
    setShowCompleteTaskModal(false);
    setPendingCompleteIds([]);

    // Nothing was sent, so the cards are only on the Complete lane optimistically — refreshing is what
    // puts them back in the lanes they were dragged from.
    refresh();
  }, [refresh]);

  const completeTaskLabel =
    pendingCompleteIds.length === 1
      ? (getTask(pendingCompleteIds[0])?.title ?? "")
      : `${pendingCompleteIds.length} tasks`;

  return {
    onTaskDrop,
    showCompleteTaskModal,
    completeTaskLabel,
    handleSubmitResolution,
    handleCancelComplete,
  };
};
